import React from 'react';
import { Controller, useFormContext, FieldValues, Path, PathValue } from 'react-hook-form';
import { TextField, InputAdornment, IconButton, Divider } from '@mui/material';
import { DownChevronBold } from 'src/components/SvgIcons';
import color from 'src/utils/colors';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import NumericFormat from 'react-number-format';

const UpChevronBold: React.FC<React.SVGAttributes<any>> = (props) => {
  return <DownChevronBold height="16px" width="16px" {...props} />;
};

type ControlledNumberInputProps = {
  label?: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  stepValue?: number;
  size?: 'small' | 'medium';
  placeholder?: string;
  sx?: Record<string, any>;
};

const ControlledNumberInput: React.FC<ControlledNumberInputProps> = ({
  label = '',
  value,
  onChange,
  min = 0,
  max = 999999,
  stepValue = 1,
  size = 'small',
  placeholder = '',
  sx = {}
}) => {
  // Handle increment with constraints
  const handleIncrement = () => {
    const newValue = Math.min((value ?? 0) + stepValue, max);
    onChange(newValue);
  };

  // Handle decrement with constraints
  const handleDecrement = () => {
    const newValue = Math.max((value ?? 0) - stepValue, min);
    onChange(newValue);
  };

  const isSmall = size === 'small';

  return (
    <>
      {label ? (
        <Text variant="body2" sx={{ fontWeight: 500, marginBottom: '8px', display: 'block' }}>
          {label}
        </Text>
      ) : null}
      <NumericFormat
        customInput={TextField}
        variant="outlined"
        value={value}
        placeholder={placeholder}
        onChange={(e) => {
          e.preventDefault();
        }}
        onValueChange={(values) => {
          const newValue = values.floatValue ?? 0;
          onChange(Math.min(max, Math.max(min, newValue)));
        }}
        onBlur={() => {
          // Clamp the value when the input loses focus
          const clampedValue = Math.min(max, Math.max(min, value));
          if (clampedValue !== value) {
            onChange(clampedValue);
          }
        }}
        InputProps={{
          inputProps: {
            min,
            max,
            step: stepValue
          },
          endAdornment: (
            <InputAdornment
              position="end"
              sx={{
                flexDirection: 'column',
                height: '100%',
                borderLeft: `1px solid ${color.graysSecondary}`
              }}
            >
              <IconButton
                onClick={handleIncrement}
                sx={{
                  padding: 0,
                  flex: 1, // Take exactly half the space
                  width: '100%',
                  borderRadius: 0,
                  '&:hover': {
                    backgroundColor: 'transparent'
                  }
                }}
                disabled={value === max}
              >
                <UpChevronBold
                  style={{
                    height: '12px',
                    width: '12px',
                    transform: 'rotate(180deg)'
                  }}
                />
              </IconButton>
              <Divider
                sx={{
                  width: '100%',
                  borderColor: color.graysSecondary,
                  margin: 0,
                  height: '1px'
                }}
              />
              <IconButton
                onClick={handleDecrement}
                sx={{
                  padding: 0,
                  flex: 1, // Take exactly half the space
                  width: '100%',
                  borderRadius: 0,
                  '&:hover': {
                    backgroundColor: 'transparent'
                  }
                }}
                disabled={value === min}
              >
                <DownChevronBold
                  style={{
                    height: '12px',
                    width: '12px'
                  }}
                />
              </IconButton>
            </InputAdornment>
          )
        }}
        sx={{
          border: `1px solid ${color.graysSecondary}`,
          borderRadius: '4px',
          width: 'fit-content',
          height: isSmall ? '40px' : '48px',
          '& .MuiInputBase-root': {
            padding: 0,
            height: '100%'
          },
          '& input': {
            fontSize: '14px',
            lineHeight: '20px',
            width: isSmall ? '48px' : '56px',
            padding: isSmall ? '4px 0px 4px 8px' : '6px 0px 6px 12px'
          },
          '& .InputAdornment-root': {
            marginLeft: isSmall ? '6px' : '10px'
          },
          '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
            WebkitAppearance: 'none',
            margin: 0
          },
          '& input[type=number]': {
            MozAppearance: 'textfield'
          },
          ...sx
        }}
      />
    </>
  );
};

type Props<T extends FieldValues> = {
  name: Path<T>;
  label: string;
  min?: number;
  max?: number;
  disabled?: boolean;
};

/**
 * A number input component that uses react-hook-form's Controller
 * to directly bind to a specific path in the form schema
 */
function PathControlledNumberInput<T extends FieldValues>({ name, label, min, max, disabled = false }: Props<T>) {
  const { control } = useFormContext<T>();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, onChange } }) => (
        <ControlledNumberInput
          label={label}
          value={value || 0}
          onChange={disabled ? () => {} : onChange}
          min={min}
          max={max}
        />
      )}
    />
  );
}

export default PathControlledNumberInput;
